import type { FetchBaseQueryError } from '@reduxjs/toolkit/query'
import axios from 'axios'

import type {
  AttachmentResult,
  GetAttachmentArg,
  UploadAttachmentArg,
  UploadAttachmentResult,
} from '../attachment.types'
import {
  resolveGetAttachmentUrl,
  resolveUploadAttachmentUrl,
} from '../utils/attachment.utils'

import { rootApi } from '@v2/store/api/rootApi'

export { resolveGetAttachmentUrl, resolveUploadAttachmentUrl }

const customError = (error: unknown): FetchBaseQueryError => ({
  status: 'CUSTOM_ERROR',
  error: error instanceof Error ? error.message : String(error),
})

const putWithRetries = async (url: string, file: File) => {
  let lastError: unknown
  for (let attempt = 0; attempt < 3; attempt += 1) {
    try {
      await axios.put(url, file, { headers: { 'Content-Type': file.type } })
      return
    } catch (error) {
      lastError = error
    }
  }
  throw lastError
}

export const attachmentApi = rootApi.injectEndpoints({
  endpoints: (builder) => ({
    getAttachment: builder.query<AttachmentResult, GetAttachmentArg>({
      queryFn: async (
        { attachmentId = '', attachmentType },
        _api,
        _extra,
        baseQuery,
      ) => {
        const response = await baseQuery({
          url: resolveGetAttachmentUrl(attachmentId, attachmentType),
          method: 'GET',
        })
        if (response.error) return { error: response.error }
        if (!response.data || typeof response.data !== 'string') {
          return { error: customError('Attachment URL was not returned') }
        }

        try {
          const probe = await axios.get(response.data)
          return {
            data: {
              url: response.data,
              contentType: probe.headers['content-type'] || undefined,
            },
          }
        } catch {
          return { data: { url: response.data, contentType: undefined } }
        }
      },
    }),
    uploadAttachment: builder.mutation<
      UploadAttachmentResult,
      UploadAttachmentArg
    >({
      queryFn: async ({ file, attachmentType }, _api, _extra, baseQuery) => {
        const endpoint = resolveUploadAttachmentUrl(attachmentType)
        const response = await baseQuery({
          url: `${endpoint}?contentType=${encodeURIComponent(file.type)}`,
          method: 'GET',
        })
        if (response.error) return { error: response.error }
        if (!response.data || typeof response.data !== 'string') {
          return { error: customError('Presigned URL was not returned') }
        }

        try {
          await putWithRetries(response.data, file)
          return { data: { url: response.data.split('?')[0] } }
        } catch (error) {
          return { error: customError(error) }
        }
      },
    }),
  }),
})

export const { useGetAttachmentQuery, useUploadAttachmentMutation } =
  attachmentApi
